import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { Router } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import type { DefaultTheme } from 'styled-components';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { HelmetProvider } from 'react-helmet-async';
import type { AppState } from '~/reducers';
import {
  initializeApp as initializeAppAction,
  setShowOldVersionWarning as setShowOldVersionWarningAction,
} from '~/components/containers/AppContainer/reducer';
import AppErrorBoundary from '~/components/views/AppErrorBoundary/AppErrorBoundary';
import MyIntlProvider from '~/components/views/MyIntlProvider/MyIntlProvider';
import SplashScreen from '~/components/views/SplashScreen/SplashScreen';
import history from '~/store/history';
import { useNetworkStatus } from '~/hooks/use-network-status';
import OfflineBanner from '~/components/views/OfflineBanner/OfflineBanner';
import ThemeWrapper from '~/styled/ThemeWrapper';
import { useOnWebView } from '~/domain/native/hooks/use-on-webview';
import useNativeTabbarSpace from '~/hooks/use-native-tabbar-space';
import { useBrowserLocale } from '~/core/hooks/use-browser-locale';
import { useI18nReady } from '~/core/hooks/use-i18n-ready';
import { useAnalyticsUserData } from '~/domain/analytics/hooks/use-analytics-user-data';
import { useRemoteConfig } from '~/domain/analytics/hooks/use-remote-config';
import { useSetSentryUser } from '~/core/hooks/use-set-sentry-user';
import { useCookieConsentMode } from '~/domain/legal/hooks/use-cookie-consent-mode';
import CookieBanner from '~/domain/legal/components/CookieBanner/CookieBanner';
import { normalizeError } from '~/core/utils';
import { useAppStatusConfig } from '~/core/components/incident-communication/hooks/use-app-status-config';
import { StatusMode } from '~/core/components/incident-communication/models/AppStatusConfig';
import IncidentLockdown from '~/core/components/incident-communication/components/IncidentLockdown/IncidentLockdown';
import App from './components/App';
import LangHelmet from './components/LangHelmet';

const renderSentryFallback: Sentry.FallbackRender = ({ error }) => {
  return <AppErrorBoundary error={normalizeError(error)} />;
};

interface StateProps {
  showOldVersionWarning: boolean;
  isUserInfoFetched: boolean;
  isAuthenticated: boolean;
  isInitializingApp: boolean;
  isInfoModalOpen: boolean;
  landingPage: string;
  theme: DefaultTheme;
}

interface DispatchProps {
  setShowOldVersionWarning: typeof setShowOldVersionWarningAction;
  initializeApp: typeof initializeAppAction;
}

export type Props = StateProps & DispatchProps;
export const AppContainer = ({
  initializeApp,
  isInitializingApp,
  isAuthenticated,
  isUserInfoFetched,
  showOldVersionWarning,
  isInfoModalOpen,
  landingPage,
  setShowOldVersionWarning,
  theme,
}: Props) => {
  const online = useNetworkStatus();
  useOnWebView();
  useNativeTabbarSpace();
  const onWebView = useSelector((state: AppState) => state.native.onWebView);
  const { locale } = useBrowserLocale();
  const i18nReady = useI18nReady();

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);
  useAnalyticsUserData();
  useRemoteConfig();
  useSetSentryUser();
  useCookieConsentMode();

  const { statusMode } = useAppStatusConfig();
  const isIncidentLockdown = onWebView === false && statusMode === StatusMode.FullLockdown;

  const isAuthenticatedAndNotFetched = isAuthenticated && !isUserInfoFetched;

  const isUptimeLogout = globalThis.location.pathname === '/uptime/logout';
  const shouldShowSplashScreen =
    !isUptimeLogout && (isInitializingApp || isAuthenticatedAndNotFetched || !i18nReady);

  let appContent: React.ReactNode;
  if (isIncidentLockdown) {
    appContent = <IncidentLockdown />;
  } else if (shouldShowSplashScreen) {
    appContent = <SplashScreen />;
  } else {
    appContent = (
      <Sentry.ErrorBoundary fallback={renderSentryFallback}>
        {!onWebView && !online && <OfflineBanner />}
        <App
          showOldVersionWarning={showOldVersionWarning}
          setShowOldVersionWarning={setShowOldVersionWarning}
          isInfoModalOpen={isInfoModalOpen}
          landingPage={landingPage}
        />
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <HelmetProvider>
      <MyIntlProvider>
        <ThemeWrapper theme={theme}>
          <Router history={history}>
            <CompatRouter>
              <LangHelmet locale={locale} />
              {appContent}
              {!onWebView && <CookieBanner />}
            </CompatRouter>
          </Router>
        </ThemeWrapper>
      </MyIntlProvider>
    </HelmetProvider>
  );
};

const mapDispatchToProps: DispatchProps = {
  setShowOldVersionWarning: setShowOldVersionWarningAction,
  initializeApp: initializeAppAction,
};

function mapStateToProps({ app, authentication, infoModal }: AppState): StateProps {
  const { showOldVersionWarning, isInitializingApp, theme } = app;
  const { isUserInfoFetched, authenticated, landingPage } = authentication;
  const { isInfoModalOpen } = infoModal;

  return {
    showOldVersionWarning,
    isUserInfoFetched,
    isInitializingApp,
    isInfoModalOpen,
    landingPage,
    isAuthenticated: authenticated,
    theme,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
